import React, { useState } from "react";
import { Box, Button, Checkbox, Dialog, DialogContent, Typography } from "@mui/material";

const CLAUSE_TITLE_COLOR = "#1f2733";
const CLAUSE_BODY_COLOR = "#48515e";
const LINE_COLOR = "#e4e8ee";
const BADGE_BG = "#eaf3ff";
const BADGE_INK = "#1466c4";

function Clause({ index, title, children }) {
	return (
		<Box
			sx={{
				display: "flex",
				gap: "12px",
				padding: "10px 0",
				borderBottom: index === 10 ? "none" : `1px solid ${LINE_COLOR}`,
			}}
		>
			<Box
				sx={{
					flexShrink: 0,
					width: "22px",
					height: "22px",
					borderRadius: "50%",
					backgroundColor: BADGE_BG,
					color: BADGE_INK,
					fontSize: "10.5px",
					fontWeight: 700,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				{index}
			</Box>
			<Typography sx={{ fontSize: "12px", color: CLAUSE_BODY_COLOR, lineHeight: 1.55 }}>
				<Box component="span" sx={{ color: CLAUSE_TITLE_COLOR, fontWeight: 700 }}>
					{title}
				</Box>{" "}
				{children}
			</Typography>
		</Box>
	);
}

export default function AgreementTermsAndConditions({ open, handleClose, onAgree, companyDetails }) {
	const [agreed, setAgreed] = useState(false);
	const promoterName = companyDetails?.name;

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth={false}
			sx={{ zIndex: 2500, margin: "auto" }}
			PaperProps={{
				sx: {
					width: "800px",
					maxWidth: "90vw",
					borderRadius: "10px",
				},
			}}
		>
			<DialogContent sx={{ padding: 0 }}>
				<Box sx={{ backgroundColor: "#eaf3ff", padding: "14px 20px", textAlign: "center" }}>
					<Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1466c4", letterSpacing: "0.2px" }}>
						Terms and Conditions <span style={{ color: "#e03131" }}>*</span>
					</Typography>
				</Box>

				<Box sx={{ padding: "12px 22px 4px" }}>
					<Typography sx={{ fontSize: "11.5px", color: CLAUSE_BODY_COLOR, marginBottom: "10px" }}>
						Please read the following before proceeding with your booking payment. By continuing, you (the{" "}
						<Box component="span" sx={{ color: CLAUSE_TITLE_COLOR, fontWeight: 700 }}>
							Customer / Allottee
						</Box>
						) agree to the terms and authorisation set out below in respect of the said Flat / Unit in the Project,
						covering both amounts already paid and amounts payable hereafter.
					</Typography>

					<Clause index={1} title="Collection Agent.">
						All payments made by you are received by{" "}
						<Box component="span" sx={{ color: CLAUSE_TITLE_COLOR, fontWeight: 700 }}>
							Vilas Javdekar Real Estate Services LLP (&quot;VJRESLLP&quot;)
						</Box>{" "}
						solely in its capacity as the authorised collection and customer-administration agent of{" "}
						<Box component="span" sx={{ color: CLAUSE_TITLE_COLOR, fontWeight: 700 }}>
							{promoterName}
						</Box>{" "}
						(the &quot;Promoter&quot;). VJRESLLP is not the seller, promoter or counterparty to your booking and does
						not act as a real estate agent / broker in this transaction.
					</Clause>

					<Clause index={2} title="Authorisation to Collect.">
						You authorise VJRESLLP to receive, on behalf of the Promoter, the consideration and all other amounts
						(including applicable taxes) payable by you towards your booking.
					</Clause>

					<Clause index={3} title="Irrevocable Authorisation to Transfer.">
						You irrevocably authorise VJRESLLP to transfer the monies received from you, as per the agreed payment
						split, to the Promoter&apos;s
						<Box sx={{ paddingLeft: "10px", marginTop: "4px" }}>
							<Box sx={{ marginTop: "2px" }}>
								(a) designated{" "}
								<Box component="span" sx={{ color: CLAUSE_TITLE_COLOR, fontWeight: 700 }}>
									100% RERA Collection Account
								</Box>{" "}
								maintained for the Project; and
							</Box>
							<Box sx={{ marginTop: "2px" }}>
								(b) the corresponding{" "}
								<Box component="span" sx={{ color: CLAUSE_TITLE_COLOR, fontWeight: 700 }}>
									GST / tax account
								</Box>
								.
							</Box>
						</Box>
					</Clause>

					<Clause index={4} title="No Diversion or Set-off.">
						Monies received from you will not be applied by VJRESLLP towards its own account, fees or any obligation
						other than transfer to the Promoter&apos;s accounts as above. Any fee payable to VJRESLLP is borne by the
						Promoter and does not reduce the amount credited to your booking.
					</Clause>

					<Clause index={5} title="Valid Discharge.">
						Once your payment is transferred to the accounts specified above, VJRESLLP&apos;s responsibility in
						respect of that payment stands fully and finally discharged, and the payment shall be deemed received by
						the Promoter and credited to your booking account.
					</Clause>

					<Clause index={6} title="Ratification of Payments to Date.">
						You confirm, ratify and adopt all amounts already paid by you and received by VJRESLLP towards your
						booking prior to your acceptance of these terms. All such earlier payments are deemed to have been
						received by VJRESLLP as the Promoter&apos;s authorised collection agent on the same terms set out above,
						and their transfer to the Promoter&apos;s 100% RERA Collection Account and corresponding GST / tax
						account is confirmed by you as a valid discharge. You waive any objection to the manner of their
						collection.
					</Clause>

					<Clause index={7} title="Receipts.">
						Any receipt or acknowledgement issued for your payment will reflect collection by VJRESLLP for and on
						behalf of the Promoter.
					</Clause>

					<Clause index={8} title="Booking Terms Prevail.">
						This authorisation governs only the manner of collection and transfer of payments. The commercial terms
						of your booking, allotment, cancellation and refund are governed by the Application for Allotment /
						Agreement for Sale with the Promoter, which prevails in the event of any inconsistency.
					</Clause>

					<Clause index={9} title="Governing Law.">
						These terms are governed by the laws of India and subject to the jurisdiction of the courts at Pune,
						Maharashtra.
					</Clause>

					<Clause index={10} title="Acknowledgement &amp; Acceptance.">
						By clicking &quot;I Agree &amp; Continue&quot;, you confirm that you have read, understood and
						irrevocably agreed to the above terms and authorisation.
					</Clause>

					<Typography
						sx={{
							fontSize: "10.5px",
							color: CLAUSE_BODY_COLOR,
							backgroundColor: "#fbfcfe",
							border: `1px solid ${LINE_COLOR}`,
							borderLeft: "3px solid #0d6efd",
							borderRadius: "6px",
							padding: "9px 12px",
							marginTop: "12px",
						}}
					>
						This is a plain-language authorisation for payment collection and does not modify your Agreement for
						Sale. Please retain a copy for your records.
					</Typography>

					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: "10px",
							flexWrap: "wrap",
							padding: "14px 0 18px",
						}}
					>
						<Box sx={{ display: "flex", alignItems: "flex-start", gap: "6px", maxWidth: "430px" }}>
							<Checkbox
								size="small"
								checked={agreed}
								onChange={(e) => setAgreed(e.target.checked)}
								sx={{ padding: "2px", marginTop: "2px" }}
							/>
							<Typography sx={{ fontSize: "11px", color: CLAUSE_BODY_COLOR }}>
								I have read, understood and agree to the above Terms and Conditions and authorisation.
							</Typography>
						</Box>
						<Button
							onClick={onAgree}
							disabled={!agreed}
							sx={{
								fontSize: "11px",
								fontWeight: 700,
								letterSpacing: "0.3px",
								padding: "8px 18px",
								backgroundColor: "#0d6efd",
								color: "#fff",
								borderRadius: "6px",
								whiteSpace: "nowrap",
								"&:hover": { backgroundColor: "#0b5ed7" },
								"&.Mui-disabled": { backgroundColor: "#9fbdf0", color: "#fff" },
							}}
						>
							I AGREE &amp; CONTINUE
						</Button>
					</Box>
				</Box>
			</DialogContent>
		</Dialog>
	);
}
